import { useSyncExternalStore } from 'react';

/**
 * SF-122 Component 0 "Breakpoint alignment": the single breakpoint the new app-shell chrome
 * switches on, deliberately identical to `Sidebar.tsx`'s existing `lg:` (1024px) Tailwind
 * breakpoint — a narrower value would leave a dead band where `useAppShellPlatform()` reports
 * `'desktop'` (so `AppShellLayout` renders `Sidebar`) while `Sidebar`'s own CSS still hides it
 * (`hidden` below `lg:`), with no way to reveal it on that width.
 */
export const APP_SHELL_BREAKPOINT = 1024;

export type AppShellPlatform = 'mobile' | 'desktop';

function matchesDesktop(): boolean {
  return window.matchMedia(`(min-width: ${APP_SHELL_BREAKPOINT}px)`).matches;
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const mql = window.matchMedia(`(min-width: ${APP_SHELL_BREAKPOINT}px)`);
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
}

function getSnapshot(): AppShellPlatform {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // No browser support for matchMedia — fall back to the more feature-complete rendering
    // rather than assuming the more constrained mobile experience.
    return 'desktop';
  }
  return matchesDesktop() ? 'desktop' : 'mobile';
}

function getServerSnapshot(): AppShellPlatform {
  return 'desktop';
}

/**
 * Resolves which chrome tree (`MobileThroneShell`/`DesktopThroneShell`, and `AppShellLayout`'s
 * own desktop-only `Sidebar`) to render. Backed by a `matchMedia` subscription via
 * `useSyncExternalStore` — the React-idiomatic way to subscribe to an external browser API's
 * `change` event, sidestepping the extra render pass `useState`+`useEffect` would otherwise cost
 * on first mount.
 */
export function useAppShellPlatform(): AppShellPlatform {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
